import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};
const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
};
const btn: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'transparent',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontWeight: 600,
  cursor: 'pointer',
};

function firstOfMonth() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

export function Expenses() {
  const qc = useQueryClient();
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [syncMsg, setSyncMsg] = useState('');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['xero-expenses', from, to],
    queryFn: () => api.listXeroExpenses({ from, to }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['xero-expenses'] });

  const syncMut = useMutation({
    mutationFn: () => api.billingSync(),
    onSuccess: () => { setSyncMsg('Synced from Xero.'); invalidate(); },
    onError: (e: any) => setSyncMsg(e?.message || 'Sync failed'),
  });
  const catMut = useMutation({
    mutationFn: (v: { xeroId: string; patch: { isExpense?: boolean; category?: string } }) =>
      api.categorizeXeroExpense(v.xeroId, v.patch),
    onSuccess: invalidate,
  });
  const { data: categories = [] } = useQuery({ queryKey: ['expense-categories'], queryFn: () => api.listExpenseCategories() });
  const allocMut = useMutation({
    mutationFn: (v: { name: string; allocatePerJob: boolean }) => api.setExpenseCategoryAllocation(v.name, v.allocatePerJob),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['expense-categories'] }); invalidate(); },
  });

  const counted = rows.filter((r) => r.isExpense);
  const total = counted.reduce((s, r) => s + Number(r.total || 0), 0);
  const byCategory = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of counted) {
      const k = r.category?.trim() || 'Uncategorised';
      m.set(k, (m.get(k) || 0) + Number(r.total || 0));
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [rows]);

  return (
    <div style={{ padding: 'var(--spacing-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-3)', flexWrap: 'wrap', marginBottom: 'var(--spacing-3)' }}>
        <div>
          <h1 style={{ margin: 0 }}>Expenses</h1>
          <p style={{ color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
            Real bills &amp; receipts from Xero. Tick what counts as a business expense and give it a category —
            those drive the actual profit in Reports.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inputStyle} />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inputStyle} />
          <button style={btn} disabled={syncMut.isPending} onClick={() => { setSyncMsg(''); syncMut.mutate(); }}>
            {syncMut.isPending ? 'Syncing…' : '⟳ Sync Xero'}
          </button>
        </div>
      </div>
      {syncMsg && <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginTop: 0 }}>{syncMsg}</p>}

      <div style={{ ...cardStyle, marginBottom: 'var(--spacing-3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--spacing-3)' }}>
          <div>
            <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>Counted expenses in range</div>
            <div style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 700, color: 'var(--color-error)' }}>${total.toFixed(2)}</div>
            <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>{counted.length} of {rows.length} transactions counted</div>
          </div>
          <div style={{ display: 'grid', gap: '4px', minWidth: 220 }}>
            {byCategory.map(([cat, amt]) => (
              <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--spacing-3)', color: 'var(--color-text-muted)' }}>
                <span>{cat}</span>
                <strong style={{ color: 'var(--color-text)' }}>${amt.toFixed(2)}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      {categories.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: 'var(--spacing-3)' }}>
          <h3 style={{ margin: '0 0 var(--spacing-1)' }}>Categories</h3>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', marginTop: 0 }}>
            Tick "spread per job" for lumpy costs (fuel, chemicals) — they're pooled and averaged across jobs as a
            cost-per-job, instead of hitting the single week you bought them.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)' }}>
            {categories.map((c) => (
              <label key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-default)', padding: '4px 10px' }}>
                <input
                  type="checkbox"
                  checked={c.allocatePerJob}
                  disabled={allocMut.isPending}
                  onChange={(e) => allocMut.mutate({ name: c.name, allocatePerJob: e.target.checked })}
                />
                <span>{c.name}</span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  {c.allocatePerJob ? 'per-job' : 'this week'}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      <section style={cardStyle}>
        {isLoading ? (
          <p style={{ color: 'var(--color-text-muted)' }}>Loading…</p>
        ) : rows.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No Xero bills/receipts in this range. Try ⟳ Sync Xero or widen the dates.</p>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--spacing-1)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 110px 120px 1fr', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', textTransform: 'uppercase', color: 'var(--color-text-muted)', padding: '0 var(--spacing-2)', fontWeight: 600 }}>
              <span>Expense?</span><span>Supplier / ref</span><span>Date</span><span style={{ textAlign: 'right' }}>Amount</span><span>Category</span>
            </div>
            {rows.map((r) => (
              <ExpenseRow key={r.xeroId} r={r} onCategory={(category) => catMut.mutate({ xeroId: r.xeroId, patch: { category } })} onToggle={(isExpense) => catMut.mutate({ xeroId: r.xeroId, patch: { isExpense } })} busy={catMut.isPending} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ExpenseRow({
  r,
  onToggle,
  onCategory,
  busy,
}: {
  r: { xeroId: string; number: string | null; contactName: string | null; docDate: string | null; total: number; status: string | null; isExpense: boolean; category: string | null; source?: string; likelyContractor?: boolean };
  onToggle: (isExpense: boolean) => void;
  onCategory: (category: string) => void;
  busy: boolean;
}) {
  const [cat, setCat] = useState(r.category ?? '');
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '90px 1fr 110px 120px 1fr',
        gap: 'var(--spacing-2)',
        alignItems: 'center',
        padding: 'var(--spacing-2)',
        borderRadius: 'var(--radius-default)',
        backgroundColor: r.isExpense ? 'var(--color-surface-elevated)' : 'transparent',
        opacity: r.isExpense ? 1 : 0.55,
      }}
    >
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
        <input type="checkbox" checked={r.isExpense} disabled={busy} onChange={(e) => onToggle(e.target.checked)} />
        <span style={{ fontSize: 'var(--font-size-xs)' }}>{r.isExpense ? 'Yes' : 'No'}</span>
      </label>
      <span>
        <strong>{r.contactName ?? '(no supplier)'}</strong>
        {r.number ? <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}> · {r.number}</span> : null}
        {r.status ? <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}> · {r.status}</span> : null}
        {r.source === 'bank' ? <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}> · bank</span> : null}
        {r.likelyContractor ? (
          <span style={{ marginLeft: 6, fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-accent,#FEC553)' }} title="This supplier matches a contractor — they may already be counted in Contractor payments. Untick to avoid double-counting.">
            ⚠ contractor?
          </span>
        ) : null}
      </span>
      <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>{r.docDate ?? '—'}</span>
      <span style={{ textAlign: 'right', fontWeight: 600 }}>${Number(r.total || 0).toFixed(2)}</span>
      <input
        value={cat}
        placeholder="category…"
        onChange={(e) => setCat(e.target.value)}
        onBlur={() => { if ((cat.trim() || '') !== (r.category ?? '')) onCategory(cat.trim()); }}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        style={{ ...inputStyle, width: '100%', padding: '4px 8px', fontSize: 'var(--font-size-sm)' }}
      />
    </div>
  );
}
